(() => {
  'use strict';

  const DAY_KEYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];

  const WEEKDAY_NAMES = [
    'Sonntag',
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag'
  ];

  function parseJsonScript(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      console.error('[JK Versandcountdown] Ungültiges JSON:', error);
      return null;
    }
  }

  function berlinNowParts(timeZone) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });

    const parts = {};

    for (const part of formatter.formatToParts(new Date())) {
      if (part.type !== 'literal') {
        parts[part.type] = part.value;
      }
    }

    const ymd = `${parts.year}-${parts.month}-${parts.day}`;
    const date = ymdToUtcDate(ymd);

    return {
      ymd,
      dayIndex: date.getUTCDay(),
      dayKey: DAY_KEYS[date.getUTCDay()],
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second)
    };
  }

  function ymdToUtcDate(ymd) {
    const [year, month, day] = ymd.split('-').map(Number);

    return new Date(
      Date.UTC(year, month - 1, day, 12, 0, 0)
    );
  }

  function utcDateToYmd(date) {
    return `${date.getUTCFullYear()}-${String(
      date.getUTCMonth() + 1
    ).padStart(2, '0')}-${String(
      date.getUTCDate()
    ).padStart(2, '0')}`;
  }

  function addCalendarDays(ymd, days) {
    const date = ymdToUtcDate(ymd);

    date.setUTCDate(
      date.getUTCDate() + days
    );

    return utcDateToYmd(date);
  }

  function dayIndexForYmd(ymd) {
    return ymdToUtcDate(ymd).getUTCDay();
  }

  function parseTimeMinutes(value) {
    if (
      typeof value !== 'string' ||
      !/^\d{2}:\d{2}$/.test(value)
    ) {
      return null;
    }

    const [hour, minute] = value.split(':').map(Number);

    if (
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  }

  function formatGermanDate(ymd) {
    return new Intl.DateTimeFormat('de-DE', {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(
      ymdToUtcDate(ymd)
    );
  }

  function germanWeekday(ymd) {
    return WEEKDAY_NAMES[
      dayIndexForYmd(ymd)
    ];
  }

  function intervalForDate(ymd, intervals) {
    if (!Array.isArray(intervals)) {
      return null;
    }

    return intervals.find(
      item =>
        item &&
        item.start &&
        item.end &&
        ymd >= item.start &&
        ymd <= item.end
    ) || null;
  }

  function buildHolidaySet(holidays) {
    const set = new Set();

    if (Array.isArray(holidays)) {
      holidays.forEach(item => {
        if (item && item.date) {
          set.add(String(item.date));
        }
      });
    }

    return set;
  }

  function isWorkingDay(
    ymd,
    holidaySet,
    vacations
  ) {
    const day = dayIndexForYmd(ymd);

    if (day < 1 || day > 5) {
      return false;
    }

    if (holidaySet.has(ymd)) {
      return false;
    }

    if (
      intervalForDate(
        ymd,
        vacations
      )
    ) {
      return false;
    }

    return true;
  }

  function nextWorkingDay(
    ymd,
    holidaySet,
    vacations
  ) {
    let current = ymd;

    do {
      current =
        addCalendarDays(
          current,
          1
        );
    } while (
      !isWorkingDay(
        current,
        holidaySet,
        vacations
      )
    );

    return current;
  }

  function addWorkingDays(
    ymd,
    days,
    holidaySet,
    vacations
  ) {
    let current = ymd;
    let remaining = days;

    while (remaining > 0) {
      current =
        addCalendarDays(
          current,
          1
        );

      if (
        isWorkingDay(
          current,
          holidaySet,
          vacations
        )
      ) {
        remaining -= 1;
      }
    }

    return current;
  }

  function nextWorkingDayAfterInterval(
    interval,
    holidaySet,
    vacations
  ) {
    if (
      !interval ||
      !interval.end
    ) {
      return null;
    }

    return nextWorkingDay(
      String(interval.end),
      holidaySet,
      vacations
    );
  }

  function calculateStandardWindow(
    ymd,
    currentMinutes,
    cutoff,
    holidaySet,
    blockedVacations
  ) {
    if (
      isWorkingDay(
        ymd,
        holidaySet,
        blockedVacations
      ) &&
      cutoff !== null &&
      currentMinutes < cutoff
    ) {
      return {
        start: ymd,
        end: ymd,
        showCountdown: true
      };
    }

    const start =
      nextWorkingDay(
        ymd,
        holidaySet,
        blockedVacations
      );

    return {
      start,
      end: start,
      showCountdown: false
    };
  }

  function calculateEngravingWindow(
    ymd,
    currentMinutes,
    cutoff,
    holidaySet,
    blockedVacations
  ) {
    if (
      isWorkingDay(
        ymd,
        holidaySet,
        blockedVacations
      )
    ) {
      if (
        cutoff !== null &&
        currentMinutes < cutoff
      ) {
        return {
          start: ymd,
          end: nextWorkingDay(
            ymd,
            holidaySet,
            blockedVacations
          )
        };
      }

      const start =
        nextWorkingDay(
          ymd,
          holidaySet,
          blockedVacations
        );

      return {
        start,
        end: addWorkingDays(
          start,
          1,
          holidaySet,
          blockedVacations
        )
      };
    }

    return {
      start: addWorkingDays(
        ymd,
        1,
        holidaySet,
        blockedVacations
      ),
      end: addWorkingDays(
        ymd,
        2,
        holidaySet,
        blockedVacations
      )
    };
  }

  /*
  |--------------------------------------------------------------------------
  | Lieferzeit bei allgemeinem Betriebsurlaub
  |--------------------------------------------------------------------------
  |
  | Die Tage bis zum tatsächlichen Versand werden als Wartezeit
  | berücksichtigt.
  |
  | Mo–Fr zählen dabei als Werktage.
  | Betriebsurlaubstage zählen mit.
  | Wochenenden und eingetragene Feiertage zählen nicht.
  |
  | Danach werden die normalen 1–3 Werktage Zustellzeit addiert.
  |--------------------------------------------------------------------------
  */

  function countLeadTimeWeekdays(
    fromYmd,
    toYmd,
    holidaySet
  ) {
    if (
      !fromYmd ||
      !toYmd ||
      toYmd <= fromYmd
    ) {
      return 0;
    }

    let current = fromYmd;
    let count = 0;

    while (current < toYmd) {
      current =
        addCalendarDays(
          current,
          1
        );

      const day =
        dayIndexForYmd(
          current
        );

      if (
        day >= 1 &&
        day <= 5 &&
        !holidaySet.has(current)
      ) {
        count += 1;
      }
    }

    return count;
  }

  function renderRow(icon, text) {
    return `
      <div class="row">
        <span class="icon">
          <span class="emoji-wrapper">${icon}</span>
        </span>
        <span class="text">${text}</span>
      </div>
    `;
  }

  function initCountdown(root) {
    if (
      !root ||
      root.dataset.jkInitialized === '1'
    ) {
      return;
    }

    root.dataset.jkInitialized = '1';

    const config =
      parseJsonScript(
        root,
        '[data-jk-shipping-config]'
      );

    const productTags =
      parseJsonScript(
        root,
        '[data-jk-product-tags]'
      ) || [];

    const container =
      root.querySelector(
        '[data-jk-shipping-message-container]'
      );

    if (
      !config ||
      !container ||
      config.enabled === false
    ) {
      root.style.display = 'none';
      return;
    }

    const excludeTag =
      String(
        config.exclude_tag ||
        'no-shipping-countdown'
      );

    if (
      Array.isArray(productTags) &&
      productTags.includes(excludeTag)
    ) {
      root.style.display = 'none';
      return;
    }

    const timeZone =
      String(
        config.timezone ||
        'Europe/Berlin'
      );

    const now =
      berlinNowParts(
        timeZone
      );

    const holidaySet =
      buildHolidaySet(
        config.holidays
      );

    /*
    |--------------------------------------------------------------------------
    | Allgemeiner Betriebsurlaub
    |--------------------------------------------------------------------------
    */

    const vacations =
      Array.isArray(
        config.vacations
      )
        ? config.vacations
        : [];

    /*
    |--------------------------------------------------------------------------
    | Separater Gravururlaub
    |--------------------------------------------------------------------------
    */

    const engravingVacations =
      Array.isArray(
        config.engraving_vacations
      )
        ? config.engraving_vacations
        : [];

    const isEngraving =
      Array.isArray(productTags) &&
      productTags.includes(
        'Gravurschmuck'
      );

    const isWatchEngraving =
      Array.isArray(productTags) &&
      productTags.includes(
        'Uhrengravur'
      );

    const isPersonalizedProduct =
      isEngraving;

    const standardCutoff =
      parseTimeMinutes(
        config.standard_cutoffs?.[
          now.dayKey
        ]
      );

    const engravingCutoff =
      parseTimeMinutes(
        config.engraving_cutoffs?.[
          now.dayKey
        ]
      );

    const expressCutoff =
      parseTimeMinutes(
        config.express_cutoffs?.[
          now.dayKey
        ]
      );

    const engravingExpressCutoff =
      parseTimeMinutes(
        config.engraving_express_cutoffs?.[
          now.dayKey
        ]
      );

    const currentMinutes =
      now.hour * 60 +
      now.minute;

    const currentGeneralVacation =
      intervalForDate(
        now.ymd,
        vacations
      );

    const currentEngravingVacation =
      intervalForDate(
        now.ymd,
        engravingVacations
      );

    let shippingDateStart = null;
    let shippingDateEnd = null;

    let showCountdown = false;
    let suppressNormalShippingRow = false;

    let generalVacationRelevant = false;
    let engravingVacationRelevant = false;

    let availabilityText =
      'Sofort lieferbar, Lieferzeit ca. 1–3 Werktage';

    let html = '';

    /*
    |--------------------------------------------------------------------------
    | Aktuell laufender allgemeiner Betriebsurlaub
    |--------------------------------------------------------------------------
    */

    if (currentGeneralVacation) {
      const firstShippingDay =
        nextWorkingDayAfterInterval(
          currentGeneralVacation,
          holidaySet,
          vacations
        );

      shippingDateStart =
        firstShippingDay;

      shippingDateEnd =
        firstShippingDay;

      suppressNormalShippingRow =
        true;

      generalVacationRelevant =
        true;

      html += renderRow(
        '📦',
        `Versand ab <strong>${formatGermanDate(
          firstShippingDay
        )}</strong> aufgrund von Urlaub.`
      );

    /*
    |--------------------------------------------------------------------------
    | Gravurschmuck
    |--------------------------------------------------------------------------
    */

    } else if (
      isPersonalizedProduct
    ) {
      const noVacationsWindow =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          []
        );

      const generalOnlyWindow =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          vacations
        );

      const engravingOnlyWindow =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          engravingVacations
        );

      const actualWindow =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          [
            ...vacations,
            ...engravingVacations
          ]
        );

      shippingDateStart =
        actualWindow.start;

      shippingDateEnd =
        actualWindow.end;

      /*
      | Allgemeiner Urlaub und Gravururlaub werden getrennt erkannt.
      */

      generalVacationRelevant =
        generalOnlyWindow.start !==
          noVacationsWindow.start ||
        actualWindow.start !==
          engravingOnlyWindow.start;

      engravingVacationRelevant =
        engravingOnlyWindow.start !==
          noVacationsWindow.start ||
        actualWindow.start !==
          generalOnlyWindow.start;

      /*
      | Separater Gravururlaub:
      |
      | ✅ Auf Lager, Lieferzeit ca. X–Y Werktage
      | 📦 Versand ab ... aufgrund von Urlaub.
      */  

      if (
        engravingVacationRelevant
      ) {
        suppressNormalShippingRow =
          true;

        html += renderRow(
          '📦',
          `Versand ab <strong>${formatGermanDate(
            actualWindow.start
          )}</strong> aufgrund von Urlaub.`
        );
      }

    /*
    |--------------------------------------------------------------------------
    | Normale Produkte / Uhrengravur
    |--------------------------------------------------------------------------
    */

    } else {
      const normalWindow =
        calculateStandardWindow(
          now.ymd,
          currentMinutes,
          standardCutoff,
          holidaySet,
          []
        );

      const actualWindow =
        calculateStandardWindow(
          now.ymd,
          currentMinutes,
          standardCutoff,
          holidaySet,
          vacations
        );

      shippingDateStart =
        actualWindow.start;

      shippingDateEnd =
        actualWindow.end;

      showCountdown =
        actualWindow.showCountdown;

      /*
      | Nur wenn der allgemeine Betriebsurlaub den Versand
      | wirklich verschiebt, wird die Lieferzeit angepasst.
      */

      generalVacationRelevant =
        actualWindow.start !==
        normalWindow.start;
    }

/*
|--------------------------------------------------------------------------
| Erste Zeile / Lieferzeit
|--------------------------------------------------------------------------
|
| Die dynamische Lieferzeit wird angepasst, wenn:
|
| 1. ein allgemeiner Betriebsurlaub den Versand verschiebt
|
| ODER
|
| 2. bei Gravurschmuck ein separater Gravururlaub
|    den Versand verschiebt.
|
| Uhrengravur ist ausdrücklich ausgenommen:
| Der Gravururlaub verändert dort NICHT die Lieferzeit der Uhr.
|--------------------------------------------------------------------------
*/

const delayedByRelevantVacation =
  generalVacationRelevant ||
  (
    isPersonalizedProduct &&
    engravingVacationRelevant
  );

if (
  delayedByRelevantVacation &&
  shippingDateStart
) {

  /*
  | Werktage bis zum tatsächlichen Versand zählen.
  |
  | Urlaubstage unter der Woche zählen als Wartezeit.
  | Samstage/Sonntage und eingetragene Feiertage zählen nicht.
  |
  | Danach kommen die normalen 1–3 Werktage
  | für die Zustellung hinzu.
  */

  const waitingWeekdays =
    countLeadTimeWeekdays(
      now.ymd,
      shippingDateStart,
      holidaySet
    );

  const deliveryMin =
    waitingWeekdays + 1;

  const deliveryMax =
    waitingWeekdays + 3;

  availabilityText =
    `Auf Lager, Lieferzeit ca. ${deliveryMin}–${deliveryMax} Werktage`;
}

    html =
      renderRow(
        '✅',
        availabilityText
      ) + html;

    /*
    |--------------------------------------------------------------------------
    | Express-Hinweis
    |--------------------------------------------------------------------------
    */

    const expressHintDays =
      Array.isArray(
        config.express_hint_days
      )
        ? config.express_hint_days
        : [
            'monday',
            'tuesday',
            'wednesday',
            'thursday'
          ];

    const expressDayAllowed =
      expressHintDays.includes(
        now.dayKey
      );

    const tomorrowYmd =
      addCalendarDays(
        now.ymd,
        1
      );

    /*
    | Für Express zählt nur:
    |
    | - Montag bis Donnerstag
    | - heute kein Betriebsurlaub
    | - morgen regulärer Werktag / kein Feiertag
    |
    | Ein morgen beginnender Urlaub verhindert NICHT,
    | dass ein heute verschicktes Expresspaket zugestellt wird.
    */

    const tomorrowWorkingForExpress =
      isWorkingDay(
        tomorrowYmd,
        holidaySet,
        []
      );

    let expressPossible =
      false;

    if (
      !currentGeneralVacation &&
      !currentEngravingVacation &&
      expressDayAllowed &&
      isWorkingDay(
        now.ymd,
        holidaySet,
        vacations
      )
    ) {
      if (
        isPersonalizedProduct
      ) {
        expressPossible =
          !currentEngravingVacation &&
          tomorrowWorkingForExpress &&
          engravingExpressCutoff !== null &&
          currentMinutes <
            engravingExpressCutoff;

      } else {
        expressPossible =
          tomorrowWorkingForExpress &&
          standardCutoff !== null &&
          expressCutoff !== null &&
          currentMinutes >=
            standardCutoff &&
          currentMinutes <
            expressCutoff;
      }
    }

    /*
    |--------------------------------------------------------------------------
    | Normale Versandzeile
    |--------------------------------------------------------------------------
    */

    if (
      !showCountdown &&
      !suppressNormalShippingRow &&
      shippingDateStart &&
      shippingDateEnd
    ) {
      let shippingText;

      if (
        shippingDateStart ===
        shippingDateEnd
      ) {
        shippingText =
          `Versand am ${germanWeekday(
            shippingDateStart
          )}, den <strong>${formatGermanDate(
            shippingDateStart
          )}.</strong>`;

      } else {
        shippingText =
          `Versand zwischen <strong>${germanWeekday(
            shippingDateStart
          )}, ${formatGermanDate(
            shippingDateStart
          )}</strong> – <strong>${germanWeekday(
            shippingDateEnd
          )}, ${formatGermanDate(
            shippingDateEnd
          )}</strong>`;
      }

      html += renderRow(
        '📦',
        shippingText
      );
    }

    /*
    |--------------------------------------------------------------------------
    | Countdown
    |--------------------------------------------------------------------------
    */

    if (showCountdown) {
      html += `
        <div class="row" id="countdown-row">
          <span class="icon">
            <span class="emoji-wrapper">⏰</span>
          </span>

          <span class="text">
            <strong>Versand noch heute</strong>,
            bestellen Sie innerhalb
            <strong id="countdown-timer"></strong>
            bis Versandschluss.
          </span>
        </div>
      `;
    }

    /*
    |--------------------------------------------------------------------------
    | Express
    |--------------------------------------------------------------------------
    */

    if (expressPossible) {
      html += renderRow(
        '⚡',
        'Expressversand (DE) <strong>heute noch möglich</strong>'
      );
    }

    /*
    |--------------------------------------------------------------------------
    | Uhrengravur
    |--------------------------------------------------------------------------
    |
    | Der normale Versand der Uhr bleibt unverändert.
    |
    | Nur wenn der separate Gravururlaub die Gravur tatsächlich
    | verschiebt, kommt der zusätzliche Hinweis.
    |--------------------------------------------------------------------------
    */

    if (isWatchEngraving) {
      const watchWithoutEngravingVacation =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          vacations
        );

      const watchWithEngravingVacation =
        calculateEngravingWindow(
          now.ymd,
          currentMinutes,
          engravingCutoff,
          holidaySet,
          [
            ...vacations,
            ...engravingVacations
          ]
        );

      if (
        watchWithEngravingVacation.start !==
        watchWithoutEngravingVacation.start
      ) {
        html += renderRow(
          'ℹ️',
          `Hinweis: Bei Auswahl einer Uhrendeckelgravur erfolgt der Versand ab <strong>${formatGermanDate(
            watchWithEngravingVacation.start
          )}</strong>.`
        );
      }
    }

    /*
    |--------------------------------------------------------------------------
    | Ausgabe
    |--------------------------------------------------------------------------
    */

    container.innerHTML =
      html;

    /*
    |--------------------------------------------------------------------------
    | Countdown laufen lassen
    |--------------------------------------------------------------------------
    */

    if (
      showCountdown &&
      standardCutoff !== null
    ) {
      const timerElem =
        root.querySelector(
          '#countdown-timer'
        );

      if (timerElem) {
        const nowSeconds =
          now.hour * 3600 +
          now.minute * 60 +
          now.second;

        const cutoffSeconds =
          standardCutoff * 60;

        const deadline =
          Date.now() +
          Math.max(
            0,
            cutoffSeconds -
              nowSeconds
          ) * 1000;

        const updateCountdown =
          () => {
            const diff =
              deadline -
              Date.now();

            if (diff <= 0) {
              timerElem.textContent =
                '0:00:00';
              return;
            }

            const hours =
              Math.floor(
                diff /
                  3600000
              );

            const minutes =
              Math.floor(
                (
                  diff %
                  3600000
                ) /
                  60000
              );

            const seconds =
              Math.floor(
                (
                  diff %
                  60000
                ) /
                  1000
              );

            timerElem.textContent =
              `${hours}:${String(
                minutes
              ).padStart(
                2,
                '0'
              )}:${String(
                seconds
              ).padStart(
                2,
                '0'
              )}`;

            requestAnimationFrame(
              updateCountdown
            );
          };

        updateCountdown();
      }
    }
  }

  function initAll(
    scope = document
  ) {
    scope
      .querySelectorAll(
        '[data-jk-shipping-countdown]'
      )
      .forEach(
        initCountdown
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        initAll(document),
      {
        once: true
      }
    );

  } else {
    initAll(document);
  }

  document.addEventListener(
    'shopify:section:load',
    event =>
      initAll(
        event.target
      )
  );
})();